import React, { useEffect, useState } from 'react';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import { getSSRAutoScript } from '@atlaskit/tokens/get-ssr-auto-script';
import { getDocument } from '@atlaskit/browser-apis';
import { getAnalyticsWebClientPromise } from '@jira/platform__observability-product-analytics-web-client-async';
import { themePreferenceResource } from '@jira/router-resources__theme-preference/src/useThemePreference.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { callWhenIdle } from '@jira/platform__utils-call-when-idle/src/index.tsx';
import { setCookie, deleteCookie } from '@atlassian/browser-storage-controls/cookie-controls';

const getDarkThemeMediaMatchQuery = (): MediaQueryList | undefined =>
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window?.matchMedia?.('(prefers-color-scheme: dark)');

function setThemeCookieWhenIdle(theme: 'light' | 'dark' | null) {
	if (!__SERVER__) {
		// this is an optimization to ensure that we don't get a white "flash" when dark mode is on, allowing
		// SSR to flush an <html> tag with the theme set.
		return callWhenIdle(() => {
			if (!theme) {
				deleteCookie('jira_theme');
			} else {
				setCookie('jira_theme', theme, {
					expires: 30,
					secure: true,
					sameSite: 'strict',
				});
			}
		}, 1000);
	}
}

export const GlobalThemeSetter = (): React.JSX.Element | null => {
	const { data, loading, error } = useResource(themePreferenceResource);
	const { colorMode } = useThemeObserver();
	const [isThemeStylesInHead, setIsThemeStylesInHead] = useState(() => {
		if (loading || error || !data) {
			// actually that's a misconfiguration and should never happen
			// but we still need to check (and it did happen in the past)
			return false;
		}

		if (__SERVER__) {
			// let's make SSR behavior more explicit - no, styles are not in the head, render them
			return false;
		}

		// check if the theme styles are already in the <head />.
		return Boolean(getDocument()?.head.querySelector('style[data-theme]'));
	});

	useEffect(() => {
		if (loading || error || !data) {
			return undefined;
		}
		const darkThemeMediaMatchQuery = getDarkThemeMediaMatchQuery();
		let cleanupIdleCallback: (() => void) | undefined;
		// Combine the themes 'dark', 'light' and 'auto' after @atlaskit/tokens@1.0.0
		// https://bitbucket.org/atlassian/atlassian-frontend/pull-requests/27634
		if (data?.theme === 'auto') {
			const token = darkThemeMediaMatchQuery?.matches ? 'dark' : 'light';
			setThemeWithToken(token, colorMode);

			cleanupIdleCallback = setThemeCookieWhenIdle(token);
			const handler = (e: MediaQueryListEvent) => {
				const tokenValue = e.matches ? 'dark' : 'light';
				setThemeWithToken(tokenValue, colorMode);
				cleanupIdleCallback?.();
				cleanupIdleCallback = setThemeCookieWhenIdle(tokenValue);
			};
			darkThemeMediaMatchQuery?.addEventListener('change', handler);
			return () => {
				darkThemeMediaMatchQuery?.removeEventListener('change', handler);
				cleanupIdleCallback?.();
			};
		}

		if (data?.theme === 'dark' || data?.theme === 'light') {
			setThemeWithToken(data?.theme, colorMode);
			cleanupIdleCallback = setThemeCookieWhenIdle(data?.theme);
		}

		if (!data?.theme) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.documentElement.removeAttribute('data-theme');

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.documentElement.removeAttribute('data-color-mode');
			cleanupIdleCallback = setThemeCookieWhenIdle(null);
		}
		return cleanupIdleCallback;
	}, [data, loading, error, colorMode]);

	useEffect(() => {
		if (loading || error || !data) {
			return;
		}

		getAnalyticsWebClientPromise().then((client) => {
			if (data?.theme) {
				client.setUIViewedAttributes({ theme: data.theme });
			} else {
				client.removeUIViewedAttribute('theme');
			}
		});
	}, [data, loading, error]);

	useEffect(() => {
		const darkThemeMediaMatchQuery = getDarkThemeMediaMatchQuery();
		getAnalyticsWebClientPromise().then((client) => {
			client.setUIViewedAttributes({
				OStheme: darkThemeMediaMatchQuery?.matches ? 'dark' : 'light',
			});
		});
	}, []);

	// @atlaskit/tokens expects the theme styles to be in the <head /> but they're
	// initially rendered in the "jira-ssr" container in the <body /> and then moved to the
	// <head />. The following prevents the styles from being re-rendered in the <body />.
	useEffect(() => {
		// this useEffect will be executed after the "move script" right below regardless of the SSR presence or absence
		// safe to say - setIsThemeStylesInHead wil be always true after the first render
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (document.head.querySelector('style[data-theme]')) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
			setIsThemeStylesInHead(true);
		}
	}, []);

	if (!data?.theme || !data?.attrs || !data?.styles) {
		return null;
	}

	const ssrAutoScript = getSSRAutoScript(data.theme);

	return (
		<>
			{!isThemeStylesInHead
				? data.styles.map((theme) => (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
						<style
							key={theme.id}
							{...theme.attrs}
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{ __html: theme.css }}
						/>
					))
				: null}
			<script
				data-defer-skip
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					__html: data.styles
						.map(
							(theme) =>
								`document.head.appendChild(document.body.querySelector('style[data-theme="${theme.id}"]'));`,
						)
						.join('\n'),
				}}
			/>
			<script
				data-defer-skip
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					__html: Object.entries(data.attrs)
						.map(([key, val]) => {
							if (key === 'data-color-mode' && ssrAutoScript) {
								return '';
							}
							return `document.documentElement.setAttribute("${key}", "${val}");`;
						})
						.join('\n'),
				}}
			/>
			{ssrAutoScript ? (
				<script
					data-defer-skip
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{
						__html: ssrAutoScript,
					}}
				/>
			) : null}
		</>
	);
};

async function setThemeWithToken(newTheme: 'light' | 'dark', currentTheme?: string) {
	if (newTheme !== currentTheme) {
		const { loadThemeTokens } = await import(
			/* webpackChunkName: "async-design-theme-tokens" */ '@jira/router-resources__theme-preference/src/loadThemeTokens.tsx'
		);
		loadThemeTokens(newTheme);
	}
}
